import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { UserStatusConst } from 'src/app/Models/userStatus.const';
import { AlertService, AlertInfo, AlertType } from 'src/app/Services/Alert/alert.service';
import { CurrentUserService } from 'src/app/Services/CurrentUser/current-user.service';
import { FloorService } from 'src/app/Services/Floor/floor.service';
import { UserService } from 'src/app/Services/User/user.service';

@Component({
  standalone: false,
  selector: 'app-edit-user-fields',
  templateUrl: './edit-user-fields.component.html',
  styleUrls: ['./edit-user-fields.component.scss'],
})
export class EditUserFieldsComponent implements OnInit, OnDestroy {
  @Input() isRegistration: boolean = false;

  email: string;
  firstName: string;
  lastName: string;

  manager: any;
  currentDivision: any;
  // Division the user had before the current selection; used to detect a real
  // change and to roll back if they cancel the booking-cancellation prompt.
  previousDivisionId: number;
  notificationTypeId: number; // Repurposed: 0 = notifications disabled, 1 = notifications enabled
  delegates: any[] = [];

  divisions: any[] = [];

  userSearchControl: UntypedFormControl = new UntypedFormControl();
  userSearchSub: Subscription;
  searchedUsers: any[] = [];
  noResultsLabel: string = 'No Results';

  divisionSearchControl: UntypedFormControl = new UntypedFormControl();
  divisionSearchSub: Subscription;
  searchedDivisions: any[] = [];
  allDivisions: any[] = [];
  divisionsLoaded: boolean = false;
  compareDivision = (a: any, b: any) => {
    if (!a || !b) {
      return a === b;
    }
    return a.id === b.id;
  };

  disableDelegate: boolean;
  delegateValue: any;
  delegateSearchControl: UntypedFormControl = new UntypedFormControl();
  delegateSearchSub: Subscription;
  searchedDelegates: any[] = [];
  delegateFeedback: string = '';
  saveSubmitted: boolean = false;
  showNotificationSettings: boolean = false;
  managerNotificationsEnabled: boolean = true;
  cancelReservationsOnDivisionChange: boolean = false;

  isChecked: boolean = false;
  isDisabled: boolean = false;

  constructor(
    private floorService: FloorService,
    private userService: UserService,
    private curUserService: CurrentUserService,
    private alertService: AlertService,
    private router: Router,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.isDisabled = this.isRegistration && (!this.isRegistration || !this.isChecked);
    this.loadDivisions();
    this.loadCurrentUser();
    this.loadDelegates();

    this.userSearchSub = this.userSearchControl.valueChanges
      .pipe(debounceTime(750))
      .subscribe((value) => this.searchForUser(value));
    this.divisionSearchSub = this.divisionSearchControl.valueChanges
      .pipe(debounceTime(750))
      .subscribe((value) => this.searchForDivision(value));
    this.delegateSearchSub = this.delegateSearchControl.valueChanges
      .pipe(
        tap((value) => {
          if (typeof value === 'string' && value.trim()) {
            this.noResultsLabel = 'Searching...';
          } else {
            this.noResultsLabel = 'No Results';
            this.searchedDelegates = [];
          }
        }),
        debounceTime(750)
      )
      .subscribe((value) => this.onDelegateSearchChanged(value));
  }

  declarationChange(checked: boolean) {
    this.isChecked = checked;
    this.isDisabled = !(this.isRegistration && this.isChecked);
  }

  save() {
    this.saveSubmitted = true;
    this.delegateFeedback = '';

    if (!this.hasRequiredProfileFields()) {
      return;
    }

    const obj = {};
    obj['emailAddress'] = this.email;
    obj['firstName'] = this.firstName;
    obj['lastName'] = this.lastName;
    obj['managerEmail'] = this.manager?.email;
    obj['userStatusId'] = UserStatusConst.Active;
    obj['divisionId'] = this.currentDivision.id;
    obj['delegations'] = this.delegates;
    if (this.showNotificationSettings) {
      // Store manager notification preference in notificationTypeId: 1 = enabled, 0 = disabled
      obj['notificationTypeId'] = this.managerNotificationsEnabled ? 1 : 0;
    }

    this.userService.updateUser(obj, this.cancelReservationsOnDivisionChange).subscribe(() => {
      this.curUserService.updateUser();
      this.sendMessageAlert();
      this.router.navigateByUrl('/');
    });
  }

  sendMessageAlert() {
    const registrationMessage = 'Your registration has been completed successfully.';
    const updateMessage = 'Your profile has been updated successfully.';
    const alertInfo: AlertInfo = {
      title: this.isRegistration ? registrationMessage : updateMessage,
      message: null,
      type: AlertType.Success,
      closeable: true,
    };
    this.alertService.setAlert(alertInfo);
  }

  searchForUser(searchStr: string) {
    this.noResultsLabel = 'Searching...';
    this.userService.searchAd(searchStr).subscribe((data) => {
      this.noResultsLabel = 'No Results';
      this.searchedUsers = data?.value ?? [];
      this.removeSelf(this.searchedUsers);
      if (this.manager) {
        //If user clears input don't clear the selected user
        this.searchedUsers.push(this.manager);
      }
    });
  }

  searchForDivision(searchStr: string) {
    const search = (searchStr || '').trim().toLowerCase();
    if (!this.divisionsLoaded) {
      this.loadDivisions();
      return;
    }
    if (!search) {
      this.divisions = this.allDivisions.slice();
      return;
    }
    this.divisions = this.allDivisions.filter((d) => d.name?.toLowerCase().includes(search));
  }
  onDivisionOpened(opened: boolean) {
    if (!opened) {
      return;
    }
    const search = (this.divisionSearchControl?.value || '').trim();
    if (!this.divisionsLoaded) {
      this.loadDivisions();
      return;
    }
    if (!search) {
      this.divisions = this.allDivisions.slice();
    }
  }

  onDelegateSearchChanged(value: any) {
    if (typeof value !== 'string') {
      return;
    }

    this.delegateValue = null;
    this.searchForDelegate(value);
  }

  searchForDelegate(searchStr: string) {
    const search = (searchStr || '').trim();
    if (!search) {
      this.noResultsLabel = 'No Results';
      this.searchedDelegates = [];
      return;
    }

    this.noResultsLabel = 'Searching...';
    this.userService.search(search).subscribe((data) => {
      this.noResultsLabel = 'No Results';
      this.searchedDelegates = data?.value ?? [];
      this.removeSelf(this.searchedDelegates);
    });
  }

  // Drop the current user from a search result list so they can't pick
  // themselves as a manager or delegate.
  removeSelf(arr: any[]) {
    const selfIndex = this.indexByEmail(arr, this.email);

    if (selfIndex >= 0) arr.splice(selfIndex, 1);
  }

  delegateSelected(val: any) {
    if (!val) {
      return;
    }

    this.delegateValue = val;
    this.delegateFeedback = '';
  }

  addDelegate() {
    const val = this.delegateValue;

    if (!val?.email) {
      this.delegateFeedback = 'Select a delegate from the search results.';
      return;
    }

    if (this.disableDelegate) {
      this.delegateFeedback = 'You can add up to 5 delegates.';
      return;
    }

    if (this.indexByEmail(this.delegates, val.email) >= 0) {
      this.delegateFeedback = 'This delegate has already been added.';
      return;
    }

    this.delegates.push(val);
    this.clearDelegateSearch();

    if (this.delegates.length >= 5) {
      this.disableDelegate = true;
      this.delegateFeedback = 'You can add up to 5 delegates.';
    } else {
      this.delegateFeedback = '';
    }
  }

  removeDelegate(index: number) {
    this.delegates.splice(index, 1);

    this.disableDelegate = this.delegates.length >= 5;
    this.delegateFeedback = '';
  }

  cancel() {
    if (!this.isRegistration) {
      this.router.navigateByUrl('/home');
    }
  }

  ngOnDestroy(): void {
    this.userSearchSub?.unsubscribe();
    this.divisionSearchSub?.unsubscribe();
    this.delegateSearchSub?.unsubscribe();
  }

  indexByEmail(arr: any[], email: string) {
    for (let i = 0; i < arr?.length; i++) {
      if (arr[i].email?.toLowerCase() === email?.toLowerCase()) return i;
    }
    return -1;
  }

  // Changing division can invalidate existing bookings, so (outside of
  // registration) confirm with the user. On confirm we flag the API to cancel
  // affected reservations; on cancel we roll the selection back.
  changeDivision($event) {
    if (this.isRegistration) {
      return;
    }
    if (
      !$event?.value?.id ||
      !this.previousDivisionId ||
      $event.value.id === this.previousDivisionId
    ) {
      return;
    }
    const previousDivision = this.getDivisionById(this.previousDivisionId);

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: 'Note: Your current bookings that are associated to your division will be canceled.  You will have to rebook your workspace based on your current division.',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.cancelReservationsOnDivisionChange = true;
      } else if (previousDivision) {
        this.currentDivision = previousDivision;
      }
    });
  }

  private loadDivisions() {
    this.floorService.getDivisions()?.subscribe((data) => {
      if (!data) {
        this.divisionsLoaded = false;
        return;
      }
      const divisions = this.extractDivisions(data);
      this.allDivisions = this.sortDivisions(divisions);
      this.mergeCurrentDivision();
      if (!this.currentDivision && this.allDivisions.length > 0) {
        this.currentDivision = this.allDivisions[0];
      }
      this.divisions = this.allDivisions.slice();
      this.divisionsLoaded = true;
    });
  }
  private extractDivisions(data: any): any[] {
    const divisions = data?.valueobjs ?? data?.value ?? [];
    return Array.isArray(divisions) ? divisions : [];
  }
  private sortDivisions(divisions: any[]): any[] {
    return divisions.slice().sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
  }
  private mergeCurrentDivision() {
    if (!this.currentDivision) {
      return;
    }
    if (!this.allDivisions.find((d) => d.id === this.currentDivision.id)) {
      this.allDivisions = this.sortDivisions([...this.allDivisions, this.currentDivision]);
    }
    if (!this.divisions.find((d) => d.id === this.currentDivision.id)) {
      this.divisions = this.sortDivisions([...this.divisions, this.currentDivision]);
    }
  }

  private loadCurrentUser() {
    this.userService.getMe().subscribe((data) => {
      this.email = data?.value?.emailAddress;
      this.firstName = data?.value?.firstName;
      this.lastName = data?.value?.lastName;
      this.showNotificationSettings = data?.value?.isManager === true;
      // Load manager notification preference from notificationTypeId: 1 = enabled (default), 0 = disabled
      this.managerNotificationsEnabled = (data?.value?.notificationTypeId ?? 1) === 1;
      this.previousDivisionId = data?.value?.divisionId;
      this.currentDivision = data?.value?.division;
      this.manager = null;
      this.searchedUsers = [];
      this.cancelReservationsOnDivisionChange = false;
      this.mergeCurrentDivision();

      // The reporting manager is persisted by email, so resolve it to the full
      // AD record so the select can display and round-trip it.
      if (data?.value?.managerEmail) {
        this.userService.findAd(data?.value?.managerEmail).subscribe((managerData) => {
          this.searchedUsers.push(managerData.value);
          this.manager = managerData.value;
        });
      }
    });
  }

  private loadDelegates() {
    this.userService.getDelegates().subscribe((data) => {
      this.delegates = data?.value ?? [];
      this.disableDelegate = this.delegates?.length >= 5;
    });
  }

  private hasRequiredProfileFields(): boolean {
    return !!this.manager?.email && !!this.currentDivision?.id;
  }

  private getDivisionById(id: number) {
    return this.allDivisions.find((division) => division.id === id);
  }

  displayDelegate(delegate: any): string {
    return delegate?.name || '';
  }

  get hasDelegateSearchText(): boolean {
    const value = this.delegateSearchControl.value;
    return typeof value === 'string' && value.trim().length > 0;
  }

  private clearDelegateSearch() {
    this.delegateValue = null;
    this.searchedDelegates = [];
    this.delegateSearchControl.setValue('', { emitEvent: false });
  }

  get showManagerRequiredError(): boolean {
    return this.saveSubmitted && !this.manager?.email;
  }

  get showDivisionRequiredError(): boolean {
    return this.saveSubmitted && !this.currentDivision?.id;
  }
}
